<template>
  <div
    @dblclick="openSettingSubtitle"
    :class="[
      'container-subtitle-content',
      {
        'background-black': settingColorBackground === 'BLACK',
      },
    ]"
  >
    <div>
      <video ref="videoElement" autoplay playsinline></video>
      <div>
        <div class="pane-top">
          <component
            :is="componentTop.component"
            v-bind="componentTop.props"
            :key="componentTop.key"
            v-show="componentTop.show"
            @close="closeSettingSubtitle"
          />
        </div>
        <div class="pane-bottom">
          <component
            :is="componentBottom.component"
            v-bind="componentBottom.props"
            :key="componentBottom.key"
            v-show="componentBottom.show"
            @close="closeSettingSubtitle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watch, computed } from 'vue';

import { storeToRefs } from 'pinia';

import SubtitleMessageText from '@/components/domains/chat/chat-subtitle/SubtitleMessageText.vue';
import SubtitleSettings from '@/components/domains/chat/chat-subtitle/SubtitleSettings.vue';
import { useMessagesSubtitle } from '@/composables/domains/chat/useMessagesSubtitle';
import { useCamera } from '@/composables/shared/useCamera';
import { SocketState, useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { ChatMessage } from '@/types/socket';
import {
  ij_resetSubtitle,
  ij_setSettingColorBackground,
  ij_setSettingIsVisibleLogo,
  ij_settingColorBackground,
  ij_settingIsVisibleLogo,
} from '@/utils/injectionKeys';

const prevMyMessageId = ref(''); //최근 내 말풍선
const messageList = ref<Array<ChatMessage>>([]);
const settingMaxWidth = ref(1800); // 자막 너비
const settingMaxWidthLimit = ref(0); // 자막 너비
const settingColorBackground = ref<string>('GREEN');
const settingIsVisibleLogo = ref<boolean>(true); // 스콘 로고 보임 여부
const isVisibleSettingSubtitle = ref<boolean>(true); // 자막 설정 창 보임 여부

const { videoElement } = useCamera();
const { messageMap, handleMessageWebSocket } = useMessagesSubtitle({
  messageList,
  prevMyMessageId,
});
const socketStore = useSocketStore();
const userStore = useUserStore();

const { socket, socketState } = storeToRefs(socketStore);

const isSettingAbove = computed(() => userStore.userPreferences.subtitleIsSettingAbove);

const componentTop = computed(() => {
  const component = isSettingAbove.value ? SubtitleSettings : SubtitleMessageText;
  const props = isSettingAbove.value
    ? { show: isVisibleSettingSubtitle.value, onClose: closeSettingSubtitle } // adjust to your API
    : { messageList: messageList.value, position: 'top' };
  return {
    component,
    props,
    key: 'top',
    show: isSettingAbove.value ? isVisibleSettingSubtitle.value : true,
  };
});

const componentBottom = computed(() => {
  const component = isSettingAbove.value ? SubtitleMessageText : SubtitleSettings;
  const props = isSettingAbove.value
    ? { messageList: messageList.value, position: 'bottom' }
    : { show: isVisibleSettingSubtitle.value, onClose: closeSettingSubtitle }; // adjust to your API;
  return {
    component,
    props,
    key: 'bottom',
    show: isSettingAbove.value ? true : isVisibleSettingSubtitle.value,
  };
});

const setSettingColorBackground = (newVal: string) => {
  settingColorBackground.value = newVal;
};

const setSettingIsVisibleLogo = (newVal: boolean) => {
  settingIsVisibleLogo.value = newVal;
};

const openSettingSubtitle = () => {
  isVisibleSettingSubtitle.value = true;
};

const closeSettingSubtitle = () => {
  isVisibleSettingSubtitle.value = false;
};

// 자막 삭제
const onClickResetSubtitle = () => {
  messageMap.value = {} as ChatMessage;
  messageList.value = [];
};

// 화면 너비에 따라 자막 너비 설정 최댓값 갱신
const setMaxWidthLimit = () => {
  settingMaxWidthLimit.value =
    window.innerWidth - parseFloat(getComputedStyle(document.documentElement).fontSize) * 1.5;

  if (settingMaxWidth.value > settingMaxWidthLimit.value) {
    settingMaxWidth.value = settingMaxWidthLimit.value;
  }
};

window.addEventListener('resize', setMaxWidthLimit);
setMaxWidthLimit();

watch(
  () => socketState.value,
  (state) => {
    if (SocketState.isOpen(state)) {
      socket.value!.onmessage = (event) => {
        handleMessageWebSocket(event);
      };
    }
  },
  { immediate: true }
);

watch(
  () => userStore.userPreferences.subtitleBackgroundColor,
  (newVal) => {
    if (newVal) {
      setSettingColorBackground(newVal);
    }
  }
);

provide(ij_settingColorBackground, settingColorBackground);
provide(ij_setSettingColorBackground, setSettingColorBackground);
provide(ij_settingIsVisibleLogo, settingIsVisibleLogo);
provide(ij_setSettingIsVisibleLogo, setSettingIsVisibleLogo);
provide(ij_resetSubtitle, onClickResetSubtitle);
</script>

<style scoped lang="scss">
.container-subtitle-content {
  width: 100%;
  height: 100dvh;
  background-color: var(--background-subtitle);
  display: flex;
}

.background-black {
  background-color: #222222;
}

.mic-input-container {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 999;
}

.icon-top {
  top: 1rem;
  margin-left: 1rem;
  cursor: pointer;
}

.icon-setting {
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
}

video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: 0;
}

.pane-top {
  height: 50vh;
  width: 100vw;
  position: relative;
}

.pane-bottom {
  width: 100vw;
  height: 50vh;
  display: flex;
  align-items: flex-end;
}
</style>
